export interface Tutorial {
  id: string;
  title: string;
  summary: string;
  cover: string;
  level: '入门' | '进阶' | '专题技巧';
  authorId: string;
  publishDate: string;
  content: { heading: string; text: string; image?: string }[];
  tags: string[];
}

export const tutorials: Tutorial[] = [
  {
    id: 't01',
    title: '新手如何用一部手机拍出电影感',
    summary: '不需要昂贵设备，掌握这几个构图和光线技巧就能让手机照片质感翻倍。',
    cover: 'https://readdy.ai/api/search-image?query=smartphone%20photography%20tutorial%20scene%20with%20warm%20orange%20cinematic%20lighting%2C%20hands%20holding%20phone%20shooting%20sunset%2C%20editorial%20style&width=800&height=500&seq=tut-t01&orientation=landscape',
    level: '入门',
    authorId: 'u01',
    publishDate: '2026-09-18',
    tags: ['手机摄影', '构图', '入门'],
    content: [
      { heading: '一、找到你的光', text: '清晨和黄昏的光线最柔和，侧光和逆光都能带来戏剧性效果，尽量避免正午顶光。' },
      { heading: '二、三分法构图', text: '把主体放在画面三分之一处，留白更有呼吸感，画面也更耐看。' },
      { heading: '三、善用前景', text: '用树叶、门框等元素做前景遮挡，能增加画面的空间层次。', image: 'https://readdy.ai/api/search-image?query=photography%20composition%20example%20with%20foreground%20leaves%20framing%20warm%20orange%20sunset%20scene%2C%20tutorial%20illustration%20style&width=1200&height=700&seq=tut-t01-b&orientation=landscape' },
    ],
  },
  {
    id: 't02',
    title: '人像摄影的眼神光怎么打才自然',
    summary: '从反光板位置到窗边自然光，教你用最简单的方式打出有神的眼神光。',
    cover: 'https://readdy.ai/api/search-image?query=portrait%20photography%20lighting%20tutorial%20with%20reflector%20and%20catchlight%20in%20eyes%2C%20warm%20studio%20setup%2C%20editorial%20style&width=800&height=500&seq=tut-t02&orientation=landscape',
    level: '进阶',
    authorId: 'u01',
    publishDate: '2026-09-10',
    tags: ['人像', '布光', '进阶'],
    content: [
      { heading: '一、认识眼神光', text: '眼神光是光源在眼球上的反射点，它能让人物显得更有精神和故事感。' },
      { heading: '二、窗边自然光的用法', text: '让模特侧对窗户45度角，窗户就是最天然免费的柔光箱。' },
      { heading: '三、反光板的角度', text: '反光板放在眼睛下方约45度，可以补充阴影区域的细节，同时在眼睛里形成光斑。' },
    ],
  },
  {
    id: 't03',
    title: '风光摄影的滤镜与曝光组合',
    summary: '渐变镜、ND镜怎么选？一次讲清楚风光摄影里最常见的几种滤镜用法。',
    cover: 'https://readdy.ai/api/search-image?query=landscape%20photography%20tutorial%20with%20camera%20filters%20on%20tripod%2C%20warm%20golden%20hour%20mountain%20scene%2C%20editorial%20style&width=800&height=500&seq=tut-t03&orientation=landscape',
    level: '专题技巧',
    authorId: 'u02',
    publishDate: '2026-09-02',
    tags: ['风光', '滤镜', '专题'],
    content: [
      { heading: '一、渐变镜解决天地光比', text: '当天空和地面光比过大时，渐变镜能压暗天空，保留地面细节。' },
      { heading: '二、ND镜拍出丝滑流水', text: '长曝光拍摄流水或云层时，ND镜可以降低进光量，延长曝光时间。' },
      { heading: '三、如何选择镜片档位', text: '一般户外日出日落用ND8即可，正午强光可以用到ND64以上。' },
    ],
  },
  {
    id: 't04',
    title: '微距摄影对焦技巧全解析',
    summary: '景深浅、对焦难是微距新手最大的烦恼，这篇文章帮你一次解决。',
    cover: 'https://readdy.ai/api/search-image?query=macro%20photography%20tutorial%20with%20camera%20and%20insect%20subject%2C%20warm%20orange%20tone%2C%20editorial%20instructional%20style&width=800&height=500&seq=tut-t04&orientation=landscape',
    level: '进阶',
    authorId: 'u03',
    publishDate: '2026-08-25',
    tags: ['微距', '对焦', '进阶'],
    content: [
      { heading: '一、使用手动对焦', text: '微距场景景深极浅，自动对焦容易跑焦，建议切换到手动对焦并配合放大取景。' },
      { heading: '二、堆焦技术', text: '拍摄多张不同对焦点的照片后期堆栈合成，可以获得更大的景深范围。' },
      { heading: '三、稳定器材不可少', text: '微距拍摄对震动极其敏感，三脚架和快门线是标配。' },
    ],
  },
  {
    id: 't05',
    title: '街拍如何做到"不被发现"',
    summary: '分享几个街头抓拍的实用心法，拍出自然真实又不冒犯他人的瞬间。',
    cover: 'https://readdy.ai/api/search-image?query=street%20photography%20tutorial%20scene%20with%20photographer%20candidly%20shooting%20in%20city%2C%20warm%20tone%2C%20editorial%20documentary%20style&width=800&height=500&seq=tut-t05&orientation=landscape',
    level: '专题技巧',
    authorId: 'u05',
    publishDate: '2026-08-18',
    tags: ['街拍', '纪实', '专题'],
    content: [
      { heading: '一、提前预判场景', text: '找一个有趣的背景提前蹲守，等待合适的人物走入画面，比到处追拍更从容。' },
      { heading: '二、用长焦保持距离', text: '35-85mm焦段既能保持安全社交距离，又能获得不错的虚化效果。' },
      { heading: '三、尊重被摄对象', text: '如果被发现，友善地打招呼并说明意图，大多数人都会愿意配合。' },
    ],
  },
  {
    id: 't06',
    title: '夜景长曝光的器材清单',
    summary: '拍好城市夜景和星空，这几样器材缺一不可，新手避坑指南。',
    cover: 'https://readdy.ai/api/search-image?query=night%20photography%20gear%20tutorial%20with%20tripod%20and%20camera%20setup%2C%20warm%20orange%20city%20lights%20background%2C%20editorial%20style&width=800&height=500&seq=tut-t06&orientation=landscape',
    level: '入门',
    authorId: 'u06',
    publishDate: '2026-08-10',
    tags: ['夜景', '器材', '入门'],
    content: [
      { heading: '一、稳固的三脚架', text: '长曝光最怕震动，一个承重足够、稳定性好的三脚架是第一道保障。' },
      { heading: '二、快门线或遥控器', text: '避免用手直接按快门造成的轻微抖动，推荐使用快门线或APP遥控拍摄。' },
      { heading: '三、备足电池', text: '长曝光和寒冷天气都会加速耗电，建议多备1-2块电池。' },
    ],
  },
];

export const getTutorialById = (id: string) => tutorials.find((t) => t.id === id);
export const getRelatedTutorials = (tutorial: Tutorial, limit = 3) =>
  tutorials
    .filter((t) => t.id !== tutorial.id && (t.level === tutorial.level || t.tags.some((tag) => tutorial.tags.includes(tag))))
    .slice(0, limit);